import { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

// Errors that are noisy in the preview/Safari but not actionable for the
// user (transient network failures, aborted in-flight fetches on route
// change, ResizeObserver loop warnings, third-party script errors, etc.).
const BENIGN_PATTERNS: RegExp[] = [
  /load failed/i,
  /failed to fetch/i,
  /networkerror/i,
  /network request failed/i,
  /aborterror/i,
  /the user aborted a request/i,
  /resizeobserver loop/i,
  /script error\.?$/i,
  /manifest\.json/i,
];

const isBenign = (text: string, src?: string) => {
  if (!text) return true;
  if (BENIGN_PATTERNS.some((re) => re.test(text))) return true;
  if (src && /manifest\.json|push-sw\.js/i.test(src)) return true;
  return false;
};

export const GlobalRuntimeMonitor = () => {
  const { toast } = useToast();

  useEffect(() => {
    const onError = (msg: any, src?: any, line?: any, col?: any, err?: any) => {
      const text = err?.message || String(msg);
      if (isBenign(text, typeof src === 'string' ? src : undefined)) {
        console.warn('[runtime] suppressed window.onerror:', text);
        return false;
      }
      console.error('window.onerror:', msg, src, line, col, err);
      toast({ title: 'Runtime error', description: text, variant: 'destructive' });
      return false;
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason =
        (e.reason && (e.reason.message || e.reason.toString())) ||
        'Unhandled promise rejection';
      if (isBenign(reason)) {
        console.warn('[runtime] suppressed unhandledrejection:', reason);
        return;
      }
      console.error('unhandledrejection:', e.reason);
      toast({ title: 'Unhandled error', description: reason, variant: 'destructive' });
    };
    window.addEventListener('error', onError as any);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError as any);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [toast]);

  // Render nothing — render-time crashes are handled by GlobalErrorBoundary.
  return null;
};
